import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import {
	RESERVATION,
	type ManagementReservationSearchTypeForQueryParams,
} from "@/routes/_constants/query-params/management";

import * as QueryRepository from "../repository/query";

import type * as Constants from "../constants";
import type * as Repository from "../repository";

type CInDateForSearchCase = {
	type: Extract<Constants.ReservationTypeForSearchReservationList, "check_in">;
	from: string;
	to: string;
};
type COutDateForSearchCase = {
	type: Extract<Constants.ReservationTypeForSearchReservationList, "check_out">;
	from: string;
	to: string;
};
type StayDateForSearchCase = {
	type: Extract<Constants.ReservationTypeForSearchReservationList, "stay_date">;
	from: string;
	to: string;
};
type UpdatedDateForSearchCase = {
	type: Extract<Constants.ReservationTypeForSearchReservationList, "updated_date">;
	from: string;
	to: string;
};
type ReservationIDForSearchCase = {
	type: Extract<Constants.ReservationTypeForSearchReservationList, "reservation_id">;
	reservationId: string;
};

export type SearchReservationListProps =
	| CInDateForSearchCase
	| COutDateForSearchCase
	| StayDateForSearchCase
	| UpdatedDateForSearchCase
	| ReservationIDForSearchCase;

export type QueryVariables = {
	hotelID: string;
} & (
	| CInDateForSearchCase
	| COutDateForSearchCase
	| StayDateForSearchCase
	| UpdatedDateForSearchCase
	| ReservationIDForSearchCase
);

type Result = {
	queryData: Repository.Query.QueryData.ReservationListQueryData;
	// 検索条件
	queryVariables: QueryVariables;
	// 予約の検索
	onSearchReservationList: (props: SearchReservationListProps) => void;
};

type Props = {
	hotelID: string;
	reservationSearchType: ManagementReservationSearchTypeForQueryParams;
	startDate: string;
	endDate: string;
	reservationID: string;
};

export const useSearchReservationList = ({
	hotelID,
	reservationSearchType,
	startDate,
	endDate,
	reservationID,
}: Props): Result => {
	const [queryVariables, setQueryVariables] = useState<QueryVariables>(
		reservationSearchType === "reservation_id"
			? {
					hotelID,
					type: "reservation_id",
					reservationId: reservationID,
				}
			: {
					hotelID,
					type: reservationSearchType,
					from: startDate,
					to: endDate,
				},
	);

	/**
	 * ローディング処理は UI で行う
	 */
	/**
	 * チェックイン日での検索
	 */
	const { queryData: queryDataForSearchByCInDate } = QueryRepository.UseCase.useReservationListByCInDateQuery({
		queryVariables: queryVariables?.type === "check_in" ? queryVariables : undefined,
	});
	/**
	 * チェックアウト日での検索
	 */
	const { queryData: queryDataForSearchByCOutDate } = QueryRepository.UseCase.useReservationListByCOutDateQuery({
		queryVariables: queryVariables?.type === "check_out" ? queryVariables : undefined,
	});
	/**
	 * 宿泊日での検索
	 */
	const { queryData: queryDataForSearchByStayDate } = QueryRepository.UseCase.useReservationListByStayDateDateQuery({
		queryVariables: queryVariables?.type === "stay_date" ? queryVariables : undefined,
	});
	/**
	 * 更新日での検索
	 */
	const { queryData: queryDataForSearchByUpdatedDate } = QueryRepository.UseCase.useReservationListByUpdatedDateQuery(
		{
			queryVariables: queryVariables?.type === "updated_date" ? queryVariables : undefined,
		},
	);
	/**
	 * 予約番号での検索
	 */
	const { queryData: queryDataForSearchByReservationID } =
		QueryRepository.UseCase.useReservationListByReservationIDQuery({
			queryVariables: queryVariables?.type === "reservation_id" ? queryVariables : undefined,
		});
	/**
	 * 検索結果
	 */
	const queryData = useMemo(
		() =>
			queryDataForSearchByCInDate ??
			queryDataForSearchByCOutDate ??
			queryDataForSearchByStayDate ??
			queryDataForSearchByUpdatedDate ??
			queryDataForSearchByReservationID ?? {
				hotelName: "",
				hotelPrefecture: "",
				reservationList: [],
			},
		[
			queryDataForSearchByCInDate,
			queryDataForSearchByCOutDate,
			queryDataForSearchByReservationID,
			queryDataForSearchByStayDate,
			queryDataForSearchByUpdatedDate,
		],
	);

	const [_, setSearchParams] = useSearchParams();

	/**
	 * 予約の検索
	 */
	const onSearchReservationList = useCallback(
		(props: SearchReservationListProps) => {
			setQueryVariables({ ...props, hotelID });

			if (!props.type) {
				return;
			}

			if (props.type === "reservation_id") {
				if (!props.reservationId) {
					return;
				}

				setSearchParams(
					(prev) => {
						prev.set(RESERVATION.SEARCH_TYPE.KEY, props.type);
						prev.set(RESERVATION.RESERVATION_ID.KEY, props.reservationId);
						return prev;
					},
					{ preventScrollReset: true },
				);
			} else {
				if (!props.from || !props.to) {
					return;
				}

				setSearchParams(
					(prev) => {
						prev.set(RESERVATION.SEARCH_TYPE.KEY, props.type);
						prev.set(RESERVATION.DATE_RANGE.START_DATE.KEY, props.from);
						prev.set(RESERVATION.DATE_RANGE.END_DATE.KEY, props.to);
						return prev;
					},
					{ preventScrollReset: true },
				);
			}
		},
		[hotelID, setSearchParams],
	);

	return {
		queryVariables,
		queryData,
		// 予約の検索
		onSearchReservationList,
	};
};
